import { useEffect, useRef, useMemo } from 'react';
import {
  generatePath,
  Outlet,
  useParams,
  NavLink,
  useLocation,
} from 'react-router-dom';
import queryString from 'query-string';
import {
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  Filters,
  useFilters,
  IconExpand,
  SchemaFilterType,
  PermissionEnum,
  useGetList,
  usePrevious,
} from '@ui/src';
import { useAppSelector } from '@store';
import { useLazyGetExchangeAutoSettingsQuery } from './store/services';
import {
  autoSettingsListColumns,
  autoSettingsListSchema,
  autoSettingsListAllSchema,
  autoSettingsListFiltersSchema,
  autoSettingsListColumnsLabels,
  autoSettingsListAllColumnsLabels,
  autoSettingsListAllFiltersSchema,
} from './variables/list';
import formatAutoSettingsList from './helpers/format-auto-settings';
import { AutoExchangeSettings } from './types';

import ROUTES from '../../routes';

import cn from './auto-settings.module.css';

const AutoSettingsList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const {
    autoSettings: {
      list: { autoSettings, pagination, errorsList },
    },
    exchangeDictionaries: { currencies, activePermissions },
  } = useAppSelector((state) => state);

  const { currency } = useParams();
  const { search } = useLocation();

  const { filters, sort, onFilterChange } = useQueryState(
    currency === 'all' ? autoSettingsListAllSchema : autoSettingsListSchema,
  );

  const [getList, { isLoading, isFetching }] =
    useLazyGetExchangeAutoSettingsQuery();

  const prevCurrency = usePrevious(currency);

  const formattedData = useMemo(() => {
    const data = formatAutoSettingsList(
      autoSettings as AutoExchangeSettings[],
      currency as string,
      currencies,
    );

    return data;
  }, [autoSettings, sort, filters, currencies]);

  const { getListAction, onFilter, setPage, errorList } =
    useGetList<AutoExchangeSettings>({
      getList,
      onFilterChange,
      onSortChange: () => {},
      schema:
        currency === 'all' ? autoSettingsListAllSchema : autoSettingsListSchema,
    });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema:
      currency === 'all'
        ? autoSettingsListAllFiltersSchema
        : autoSettingsListFiltersSchema,
    filters,
    onFilterChange: () => {},
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (filters && !autoSettings) {
      let currentFilters = filters;

      if (currency !== 'all') {
        currentFilters = {
          ...currentFilters,
          exchangeCurrencyToId: {
            value: [currency as string],
            type: SchemaFilterType.STRING,
          },
        };
      }

      if (!autoSettings) {
        getListAction({
          filters: {
            ...currentFilters,
          },
        });
      }
    } else if (prevCurrency && currency !== prevCurrency) {
      const nextFilters: Filters = {};

      if (currency !== 'all') {
        nextFilters.exchangeCurrencyFromId = {
          value: [currency as string],
          type: SchemaFilterType.STRING,
        };
      }

      if (search) {
        const parsed = queryString.parse(search.replace('?', ''));

        if (Object.keys(parsed).length) {
          Object.keys(parsed).forEach((item) => {
            nextFilters[item] = {
              value: [parsed[item] as string],
              type: SchemaFilterType.STRING,
            };
          });
        }
      }

      onFilter(nextFilters);
    }
  }, [filters, sort, autoSettings, currency, prevCurrency, search]);

  useEffect(() => {
    if (currencies) {
      let formattedCurrencies = currencies.map((item) => ({
        name: `${item.name} (${item.description})`,
        value: item.id,
      }));

      if (currency !== 'all') {
        formattedCurrencies = formattedCurrencies.filter(
          (item) => item.value !== currency,
        );
      }

      setValues('exchangeCurrencyToId', formattedCurrencies);
      setValues('exchangeCurrencyFromId', formattedCurrencies);
    }
  }, [currencies, currency]);

  useEffect(() => {
    let title = 'Auto FX settings';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  const addUrl = generatePath(ROUTES.AUTO_SETTINGS_ADD, {
    currency: currency as string,
  });

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.autoSettingsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={autoSettingsListColumns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        sort={sort}
        columnsLabels={
          currency === 'all'
            ? autoSettingsListAllColumnsLabels
            : autoSettingsListColumnsLabels
        }
        onFilterChange={(filtersValue) => {
          const nextFilters = { ...filtersValue };

          if (currency !== 'all') {
            nextFilters.exchangeCurrencyFromId = {
              value: [currency as string],
              type: SchemaFilterType.STRING,
            };
          }

          onFilter(nextFilters);
        }}
        error={errorsList || errorList}
        filtersComponent={filtersComponent}
        isLoading={isFetching}
        tableBodyClassName={`${!autoSettings?.length ? cn.emptyTable : ''} ${
          isFetching ? cn.tableLoading : ''
        }`}
        tableTheadClassName={cn.rateTableHead}
        handleSubmit={handleSubmit}
        pagination={pagination}
        take="20"
        setPage={(data) => {
          const nextFilters = {
            ...filters,
          };

          if (currency !== 'all') {
            nextFilters.exchangeCurrencyToId = {
              value: [currency as string],
              type: SchemaFilterType.STRING,
            };
          }

          setPage({ ...data, filters: nextFilters });
        }}
        topContent={
          <div className={cn.autoSettingsTop}>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_AUTO_EXCHANGE_CREATE_ACTION,
            ) && (
              <NavLink to={`${addUrl}${search || ''}`} className={cn.addRate}>
                <span>
                  <IconExpand />
                </span>
                <span>Add Pair</span>
              </NavLink>
            )}
          </div>
        }
        clickable
      />
    );
  }

  return (
    <div className={cn.autoSettingsWrapper} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default AutoSettingsList;
